// Le petit composeur d'un groupe ou d'une page.
//
// Plus simple que celui du fil : pas de type de publication. On écrit dans un
// endroit précis, et l'endroit dit déjà de quoi il s'agit — un groupe de prière
// n'a pas besoin qu'on lui répète que c'est une prière.
//
// Il est partagé par les deux écrans : un groupe et une page n'y diffèrent que
// par le champ qui rattache la publication (`groupeId` ou `pageId`) et par le
// mot de confirmation.
//
// ⚠️ LA PHOTO ET LA VIDÉO SONT EN OPTION, ET ÉTEINTES PAR DÉFAUT.
// Elles ont été ajoutées le 14 août 2026 pour les PAGES, qui n'avaient qu'un
// champ de texte : une page d'église annonce un culte, une convention, un
// concert — cela ne se raconte pas sans image. Le composeur des GROUPES, lui,
// ne bouge pas d'un pixel : `avecMedia` reste absent chez lui, et l'écran qu'il
// dessine est exactement celui d'hier. On ajoute à côté, on ne remplace rien.

import { useRef, useState } from "react";
import Store from "../store.js";
import { APP } from "../config.js";
import { toast } from "../ui.js";
import Avatar from "./Avatar.jsx";
import Icone from "./Icone.jsx";

// Comme dans le composeur du fil : au-delà, personne ne fait défiler, et l'envoi
// devient long sur une connexion mobile.
const MAX_PHOTOS = 10;

/**
 * @param {object} p
 * @param {string} p.invite         le texte d'invitation du champ
 * @param {string} p.succes         ce qu'on dit une fois publié
 * @param {object} p.champsEspace   { groupeId } ou { pageId, pageNom, pageImage }
 * @param {boolean} [p.avecMedia]   proposer la photo et la vidéo (les pages)
 */
export default function ComposeurEspace({ ctx, invite, succes, champsEspace, avecMedia = false }) {
  const [texte, setTexte] = useState("");
  const [envoi, setEnvoi] = useState(false);
  // { genre: "image", urls: [...] } ou { genre: "video", urls: [une] } — jamais
  // les deux : une vidéo au milieu d'un album ne se regarde pas.
  const [media, setMedia] = useState(null);
  const [info, setInfo] = useState("");
  const zone = useRef(null);

  const ajuster = (el) => {
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(200, el.scrollHeight) + "px";
  };

  const limiteVideo = Store.mode === "local" ? APP.videoMaxMo.local : APP.videoMaxMo.firebase;

  /* Le choix des fichiers, repris tel quel du composeur du fil — mêmes limites,
     mêmes refus, même décompte à voix haute.
     ⚠️ EN SÉRIE ET NON EN PARALLÈLE. Chaque fichier demande au serveur un jeton
     de dépôt, délivré au compte-gouttes ; entre deux photos, le pilote patiente
     quelques secondes plutôt que d'échouer. Sans « Envoi 2 sur 3… », cette
     attente n'est qu'un écran figé — et un écran figé, on le referme. */
  const choisir = async (fichiers, genre) => {
    const liste = [...(fichiers || [])].filter((f) => f.type.startsWith(genre + "/"));
    if (!liste.length)
      return toast(
        genre === "image" ? "Ce fichier n'est pas une image" : "Ce fichier n'est pas une vidéo",
        "erreur"
      );
    if (genre === "video" && liste[0].size > limiteVideo * 1024 * 1024)
      return toast(`Vidéo trop lourde (max ${limiteVideo} Mo)`, "erreur");

    const dejaLa = genre === "image" && media?.genre === "image" ? media.urls : [];
    const aFaire = genre === "video" ? liste.slice(0, 1) : liste.slice(0, MAX_PHOTOS - dejaLa.length);
    if (!aFaire.length) return toast(`${MAX_PHOTOS} photos au maximum`, "erreur");

    setInfo(
      genre === "video"
        ? "Préparation de la vidéo…"
        : `Préparation de ${aFaire.length > 1 ? aFaire.length + " photos" : "la photo"}…`
    );
    try {
      const urls = [...dejaLa];
      for (const [i, f] of aFaire.entries()) {
        if (aFaire.length > 1) setInfo(`Envoi ${i + 1} sur ${aFaire.length}…`);
        urls.push(await Store.upload(f, genre));
      }
      setMedia({ genre, urls });
    } catch (e) {
      toast(e.message || "Fichier impossible à charger", "erreur");
    }
    setInfo("");
  };

  const retirerPhoto = (i) =>
    setMedia((m) => {
      const urls = m.urls.filter((_, k) => k !== i);
      return urls.length ? { ...m, urls } : null;
    });

  // Une publication tient debout avec une seule photo et pas un mot — c'est
  // même le cas ordinaire d'une affiche de culte.
  const prete = Boolean(texte.trim() || media);

  const publier = async () => {
    const t = texte.trim();
    if (!prete || envoi) return;
    setEnvoi(true);
    try {
      await Store.posts.add({
        type: "post",
        authorId: ctx.moi.uid,
        auteurNom: ctx.moi.nom,
        auteurPhoto: ctx.moi.photo || "",
        texte: t,
        // `image` garde la PREMIÈRE photo : tout ce qui a été écrit avant les
        // albums la lit encore, et rien de l'ancien n'a besoin d'être réécrit.
        image: media?.genre === "image" ? media.urls[0] : "",
        images: media?.genre === "image" ? media.urls : [],
        video: media?.genre === "video" ? media.urls[0] : "",
        ...champsEspace,
      });
      setTexte("");
      setMedia(null);
      ajuster(zone.current);
      toast(succes);
    } catch (e) {
      toast(e.message || "Publication impossible", "erreur");
    }
    setEnvoi(false);
  };

  return (
    <section className="composeur">
      <div className="composeur__haut">
        <Avatar membre={ctx.moi} taille={40} />
        <textarea
          ref={zone}
          rows={1}
          maxLength={3000}
          placeholder={invite}
          value={texte}
          onChange={(e) => {
            setTexte(e.target.value);
            ajuster(e.target);
          }}
        />
      </div>
      {avecMedia && media && (
        <div className={`composeur__apercu${media.urls.length > 1 ? " composeur__apercu--album" : ""}`}>
          {media.genre === "video" ? (
            <>
              <video src={media.urls[0]} controls playsInline />
              <span className="composeur__retirer" onClick={() => setMedia(null)}>
                ✕ Retirer
              </span>
            </>
          ) : (
            media.urls.map((u, i) => (
              <span className="composeur__vignette" key={u}>
                <img src={u} alt="" />
                {/* chaque photo se retire seule : sinon il faut tout refaire
                    pour en enlever une */}
                <button
                  type="button"
                  className="composeur__retirer composeur__retirer--vignette"
                  aria-label={`Retirer la photo ${i + 1}`}
                  onClick={() => retirerPhoto(i)}
                >
                  ✕
                </button>
              </span>
            ))
          )}
        </div>
      )}

      <div className="composeur__bas">
        {avecMedia && (
          <>
            <label className="btn btn--fantome btn--petit">
              <Icone nom="photo" taille={19} /> Photos
              <input
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  choisir(e.target.files, "image");
                  e.target.value = "";
                }}
              />
            </label>
            <label className="btn btn--fantome btn--petit">
              <Icone nom="video" taille={19} /> Vidéo
              <input
                type="file"
                accept="video/*"
                hidden
                onChange={(e) => {
                  choisir(e.target.files, "video");
                  e.target.value = "";
                }}
              />
            </label>
          </>
        )}
        <span className="composeur__info">{avecMedia ? info : ""}</span>
        <button
          className="btn btn--primaire"
          disabled={(avecMedia ? !prete : !texte.trim()) || envoi}
          onClick={publier}
        >
          Publier
        </button>
      </div>
    </section>
  );
}
